/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { FC, useState } from "react";

import { useTranslation } from "react-i18next";
import { Button, Hidden, IconButton, useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";

import { HamburgerIcon } from "@/assets/icons";
import { HeaderSeparator } from "@/assets/images";
import { Logo } from "@/components/atoms";
import { StyledLogoGridItem, StyledMenuGridItem } from "@/components/layouts/NotAuthenticated/styled";
import { Help, LanguageSelector, ModalFullPage } from "@/components/molecules";
import { QuickSearch, QuickSearchMobile } from "@/components/organisms";
import { AppRoute } from "@/shared/domain";
import { useAuth, useNavigation } from "@/shared/hooks";
import { Scope } from "@/shared/hooks/useAuth/types";

import AccountInformation from "../AccountInformation/AccountInformation";
import HamburgerMenu from "../HamburgerMenu/HamburgerMenu";
import MyBrokerSelector from "../MyBrokerSelector/MyBrokerSelector";
import { StyledAuthHeader } from "./styled";

type AuthenticatedHeaderProps = {
    onBrokerSelect?: (broker: MyBrokerApi.Broker, availableBrokers: MyBrokerApi.Broker[]) => void;
    onRedirectTo?: (url: string) => void;
    showBrokerSelector?: boolean;
    disableSearch?: boolean;
    officeId?: string;
};

const AuthenticatedHeader: FC<AuthenticatedHeaderProps> = ({
    onBrokerSelect,
    onRedirectTo,
    disableSearch = false,
    showBrokerSelector = true,
    officeId,
}) => {
    const { navToRoot } = useNavigation();
    const { user } = useAuth();
    const [openHamburgerMenu, setOpenHamburgerMenu] = useState(false);
    const { t } = useTranslation();
    const theme = useTheme();
    const isScopedLogin = user.isScopedLogin();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const handleNavHamburgerClick = () => {
        setOpenHamburgerMenu(true);
    };
    const displaySearchInput = !disableSearch && (!isScopedLogin || user.isScope(Scope.Preview));
    const disableClickOnLogo = user.isScope([
        Scope.Tariffication,
        Scope.SignPackages,
        Scope.DataVerificationCompany,
        Scope.InvoicePreview,
    ]);

    return (
        <>
            <Logo variant="favicon" officeId={user.currentBrokerId} />
            <StyledAuthHeader>
                <div className="auth-header-container">
                    <Grid container height="100%" rowSpacing={0}>
                        <StyledLogoGridItem item xs={8} sm={4}>
                            <Button
                                variant="text"
                                aria-label="MyBroker"
                                title="MyBroker"
                                disabled={disableClickOnLogo}
                                className="logo"
                                onClick={() => (onRedirectTo ? onRedirectTo(AppRoute.Root) : navToRoot(officeId))}
                            >
                                <Logo variant="square" officeId={user.currentBrokerId} />
                            </Button>
                            {showBrokerSelector && (
                                <>
                                    <HeaderSeparator className="logo-separator" />
                                    <MyBrokerSelector onSelect={onBrokerSelect} />
                                </>
                            )}
                        </StyledLogoGridItem>

                        <StyledMenuGridItem $lightMenu={false} $lightMenuSmallInverted={false} item xs={4} sm={8}>
                            {displaySearchInput && !isSmallScreen && <QuickSearch />}
                            <div className="header-components-action">
                                <Hidden smDown>
                                    {user.isAuthenticated && <AccountInformation />}
                                    <LanguageSelector />
                                    <Help />
                                </Hidden>
                                <Hidden smUp>
                                    <IconButton
                                        onClick={handleNavHamburgerClick}
                                        data-testid="authenticated_header-button-hamburger"
                                    >
                                        <HamburgerIcon />
                                    </IconButton>
                                </Hidden>
                            </div>
                            {displaySearchInput && isSmallScreen && <QuickSearchMobile />}
                        </StyledMenuGridItem>
                    </Grid>
                </div>
            </StyledAuthHeader>

            {openHamburgerMenu && (
                <ModalFullPage
                    hideBackdrop
                    open={openHamburgerMenu}
                    onClose={() => setOpenHamburgerMenu(false)}
                    title={t("NavBar.Menu")}
                >
                    <HamburgerMenu onClose={() => setOpenHamburgerMenu(false)} />
                </ModalFullPage>
            )}
        </>
    );
};

export default AuthenticatedHeader;
